import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

function LeadAccount({ leadDetails, refreshFunc }) {
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ ...leadDetails });
  const [defaultShippingAddress, setDefaultShippingAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const updateLeadAccount = async (e) => {
    e.preventDefault();
    if (!leadDetails?.leadId) return;

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        leadId: leadDetails?.leadId,
        id: leadDetails?.id,
      };
      const updatedData = JSON.stringify(payload);
      const { data } = await API.patch(
        "/leads",
        { updatedData },
        { headers: authHeader() }
      );

      if (data?.success) {
        if (typeof refreshFunc === "function") {
          refreshFunc();
        }
        setShowModal(false);
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to update lead",
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getLeadDefaultShipAddress = useCallback(
    async (leadId) => {
      if (!leadId) return;
      try {
        setIsLoading(true);
        const { data } = await API.post(
          "/lead/ship-address/default/" + leadId,
          { isPrimary: false },
          { headers: authHeader() }
        );
        if (data?.success) {
          setDefaultShippingAddress(data?.data || {});
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch shipping address",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (leadDetails) {
      setFormData({
        addressCompany: leadDetails?.addressCompany || "",
        addressContact: leadDetails?.addressContact || "",
        addressLine1: leadDetails?.addressLine1 || "",
        addressLine2: leadDetails?.addressLine2 || "",
        addressCity: leadDetails?.addressCity || leadDetails?.city || "",
        addressState: leadDetails?.addressState || leadDetails?.state || "",
        addressZipCode: leadDetails?.addressZipCode || leadDetails?.zip || "",
        addressCountry:
          leadDetails?.addressCountry || leadDetails?.country || "",
      });
    }
  }, [leadDetails]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (leadDetails?.leadId) {
        getLeadDefaultShipAddress(leadDetails.leadId);
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [leadDetails?.leadId, getLeadDefaultShipAddress]);

  return (
    <>
      <div className="row w-100">
        <div className="col-md-12">
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="border-bottom border-secondary">BILLING ADDRESS</h4>
            <div>
              <button
                className="btn d-flex justify-content-center align-items-center btn-sm btn-success"
                style={{ width: 28, height: 28 }}
                onClick={() => setShowModal(true)}
              >
                <i className="mdi fs-5 mdi-pencil"></i>
              </button>
            </div>
          </div>
          <div className="d-flex">
            <div className="d-flex flex-column" style={{ minWidth: 110 }}>
              <div className="">Company Name</div>
              <div className="">Address Contact</div>
              <div className="">Address 1</div>
              <div className="">Address 2</div>
              <div className="">City</div>
              <div className="">State</div>
              <div className="">Zip Code</div>
              <div className="">Country</div>
            </div>

            <div className="">
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {leadDetails?.addressCompany || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {leadDetails?.addressContact || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {leadDetails?.addressLine1 || "-"}
                </p>
              </div>
              <div className="w-50">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate ">
                  {leadDetails?.addressLine2 || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {leadDetails?.addressCity || leadDetails?.city || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {leadDetails?.addressState || leadDetails?.state || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {leadDetails?.addressZipCode || leadDetails?.zip || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {leadDetails?.addressCountry || leadDetails?.country || "-"}
                </p>
              </div>
            </div>
          </div>

          <hr className="me-2" />
          <div className="d-flex justify-content-between align-items-center w-100">
            <h4 className="border-bottom border-secondary">SHIPPING ADDRESS</h4>
          </div>
          <div className="w-100 d-flex mb-2">
            <div className="d-flex flex-column" style={{ minWidth: 110 }}>
              <div className="">Company Name</div>
              <div className="">Address Line 1</div>
              <div className="">Address Line 2</div>
              <div className="">City</div>
              <div className="">State</div>
              <div className="">Zip Code</div>
              <div className="">Country</div>
            </div>

            <div className="">
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {defaultShippingAddress?.companyName || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {defaultShippingAddress?.addressLine1 || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {defaultShippingAddress?.addressLine2 || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {defaultShippingAddress?.city || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {defaultShippingAddress?.state || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {defaultShippingAddress?.zip || "-"}
                </p>
              </div>
              <div className="">
                :&nbsp;&nbsp;&nbsp;
                <p className="d-inline text-truncate">
                  {defaultShippingAddress?.country || "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Body>
          <div className="px-2 pb-2">
            <div
              style={{ width: "fit-content" }}
              className="border-bottom mb-2"
            >
              <h3 className="">Lead Account Details</h3>
            </div>

            <form onSubmit={updateLeadAccount}>
              {isLoading && <Spinner />}
              <div className="row">
                <div className="col-md-12">
                  <div className="">
                    <h4 className="mb-0">Billing Address</h4>
                  </div>
                  <hr className="mt-0" />
                  <div className="row mb-1">
                    <label className="col-3 my-auto">Company Name</label>
                    <div className="col-9 ">
                      <input
                        type="text"
                        value={formData?.addressCompany}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressCompany: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter company name"
                      />
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3 my-auto">Address Contact</label>
                    <div className="col-9 ">
                      <input
                        type="text"
                        value={formData?.addressContact}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressContact: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter address contact"
                      />
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3 my-auto">Address 1</label>
                    <div className="col-9 ">
                      <input
                        type="text"
                        value={formData?.addressLine1}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine1: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter address 1"
                      />
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3 my-auto">Address 2</label>
                    <div className="col-9 ">
                      <input
                        type="text"
                        value={formData?.addressLine2}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine2: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter address 2"
                      />
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3 my-auto">
                      City / State / Zipcode
                    </label>
                    <div className="col-9 ">
                      <div className="d-flex">
                        <input
                          type="text"
                          value={formData?.addressCity}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              addressCity: e.target.value,
                            })
                          }
                          className="form-control w-50"
                          placeholder="City"
                        />
                        <input
                          type="text"
                          value={formData?.addressState}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              addressState: e.target.value,
                            })
                          }
                          style={{ width: "20%" }}
                          className="form-control"
                          placeholder="State"
                        />
                        <input
                          type="text"
                          value={formData?.addressZipCode}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              addressZipCode: e.target.value,
                            })
                          }
                          style={{ width: "30%" }}
                          className="form-control"
                          placeholder="Zipcode"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3 my-auto">Country</label>
                    <div className="col-9 ">
                      <input
                        type="text"
                        value={formData?.addressCountry}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressCountry: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter country"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex-grow-1 d-flex justify-content-end align-items-end">
                <div>
                  <button
                    onClick={() => setShowModal(false)}
                    type="button"
                    className="btn btn-secondary me-1 px-1"
                  >
                    CLOSE
                  </button>
                  <button type="submit" className="btn btn-primary">
                    UPDATE
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default LeadAccount;
